/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import { Customers, Plans } from "App/IOC/Services";
import { Filter, FilterValue, HandleListError, Identifiable, Named } from "@Internal/Contracts";
import {
    CustomError, CustomValue, PagerSorter, SelectionFilter, TextFilter
} from "@Internal/Models";
import { Customer } from "App/Models";

let defaultFilters: Filter[] = [];

async function getByCustomer( keyword?: string ): Promise<[ Customer[], CustomError | undefined ]> {
    const customerFilter = new TextFilter( "Name", "" );
    customerFilter.Value = [ keyword !== undefined ? keyword : "" ];

    const [ customers, , error ] = await Customers.Find(
        [ customerFilter ],
        new PagerSorter()
    );

    return [ customers, error ];
}

async function getByCompany( keyword?: string ): HandleListError<Named & Identifiable> {
    const companyFilter = new TextFilter( "Company", "" );
    companyFilter.Value = [ keyword !== undefined ? keyword : "" ];

    const [ customers, , error ] = await Customers.Find( [ companyFilter ], new PagerSorter() );

    const distinctCompanies = [ ...new Set(
        customers.map( ( customer: Customer ): string => customer.Company as string )
    )];

    const companies = distinctCompanies.map( ( company: string ): Named & Identifiable =>
        ({ Id: company as string, Name: company as string })
    );

    return [ companies, error ]
}

/* Options du filtre par nom d'abonnement (plan). Un même nom de plan peut exister sous plusieurs
   produits : on regroupe donc par nom et chaque option porte tous les ids de plan correspondants
   (joints par une virgule), pour que le filtre les attrape tous côté backend. */
async function getByPlan( keyword?: string ): HandleListError<Named & Identifiable> {
    const [ plans, , error ] = await Plans.Find( [], new PagerSorter( 999 ) );

    if( error !== undefined )
        return [ [], error ];

    const term = ( keyword !== undefined ? keyword : "" ).trim().toLowerCase();

    const idsByName = new Map<string, number[]>();

    for( const plan of plans ) {
        const name = plan.Name !== undefined ? plan.Name : "";

        if( name === "" )
            continue;

        if( term !== "" && name.toLowerCase().indexOf( term ) === -1 )
            continue;

        const ids = idsByName.get( name ) || [];
        ids.push( plan.Id );
        idsByName.set( name, ids );
    }

    const options = Array.from( idsByName.entries() ).map(
        ( [ name, ids ]: [ string, number[] ] ): Named & Identifiable =>
            ({ Id: ids.join( "," ), Name: name })
    );

    return [ options, undefined ];
}

function getSubscriptionStatus(): FilterValue[] {
    return [
        new CustomValue<string>( $t( "SUBSCRIPTIONS_statusActive" ), "Active" ),
        new CustomValue<string>( $t( "SUBSCRIPTIONS_statusTrial" ), "Trial" ),
        new CustomValue<string>( $t( "SUBSCRIPTIONS_statusUnpaid" ), "Unpaid" ),
        new CustomValue<string>( $t( "SUBSCRIPTIONS_statusCanceled" ), "Canceled" ),
        new CustomValue<string>( $t( "SUBSCRIPTIONS_statusOffer" ), "Offer" )
    ]
}

function Get(): Filter[] {
    if( defaultFilters.length === 0 )
        defaultFilters = [
            SelectionFilter.Request(
                "Customer",
                $t( "GLOBAL_filterCustomerName" ),
                getByCustomer,
                $t( "GLOBAL_filterCustomerPlaceholder" )
            ),

            SelectionFilter.Multi(
                "Status",
                $t( "GLOBAL_filterStatus" ),
                getSubscriptionStatus(),
                $t( "GLOBAL_filterStatusPlaceholder" )
            ),

            SelectionFilter.Request(
                "Company",
                $t( "CUSTOMERS_filterCompany" ),
                getByCompany,
                $t( "CUSTOMERS_filterCompanyPlaceholder" )
            ),

            SelectionFilter.Request(
                "Plan",
                $t( "SUBSCRIPTIONS_filterPlan" ),
                getByPlan,
                $t( "SUBSCRIPTIONS_filterPlanPlaceholder" )
            )
        ];

    return defaultFilters;
}

export default {
    Get
};
